import {
  useEffect,
  useState,
  useRef,
  useCallback,
  type RefObject,
  type Ref,
} from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  validateIntakeKey,
  intakeUpdateUserPwd,
  intakeInsertUser,
} from "@/api/intake-request";
import authRequest from "@/api/auth-request";
import { useAuth } from "@/hooks/useAuth";
import { useProfileStore, type Patient } from "@/store/profile";
import type { IntakeUser } from "@/types/intake";
import { INTAKE_RESET_PATIENT_KEY, isResetPwdPatientKey } from "@/types/intake";
import {
  setPendingTaskResume,
  tryResumePendingTask,
  clearPendingTaskResume,
  getPendingTaskResume,
  findPatientByPendingKey,
} from "@/constants/taskLinkSession";
import { BrandedAuthShell } from "@/components/auth/BrandedAuthShell";
import { PatientListPickerContent } from "@/components/account/PatientListPickerContent";
import { SubmitButton } from "@/components/ui/SubmitButton";
import { cn } from "@/utils/cn";

const INTAKE_KEY_LEN = 50;
const FACILITY_KEY_LEN = 36;
const PATIENT_KEY_LEN = 20;

const passwordField = z
  .string()
  .min(8, "At least 8 characters")
  .regex(/[0-9]/, "Contains a number")
  .regex(/[A-Z]/, "Contains uppercase")
  .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, "Contains special character");

const resetSchema = z
  .object({
    email: z.string().min(1, "Email is required").email("Enter a valid email"),
    password: passwordField,
    confirmPassword: z.string().min(1, "Confirm password"),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const registerSchema = z
  .object({
    email: z.string().min(1, "Email is required").email("Enter a valid email"),
    password: passwordField,
    confirmPassword: z.string().min(1, "Confirm password"),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetForm = z.infer<typeof resetSchema>;
type RegisterForm = z.infer<typeof registerSchema>;

interface PatientOption {
  firstName: string;
  lastName: string;
  patientID: string;
  facilityID: string;
  facilityKey?: string;
  patientKey?: string;
  officeName?: string;
}

function validateTaskQueryParams(key: string | null, f: string | null, p: string | null): string | null {
  if (!key?.trim() || key.trim().length !== INTAKE_KEY_LEN) {
    return "This link is missing a valid key.";
  }
  if (!f?.trim() || f.trim().length !== FACILITY_KEY_LEN) {
    return "This link is missing a valid facility.";
  }
  const pt = (p ?? "").trim();
  if (!pt) return "This link is missing patient information.";
  const isReset = pt.toUpperCase() === INTAKE_RESET_PATIENT_KEY;
  if (!isReset && pt.length !== PATIENT_KEY_LEN) {
    return "This link has an invalid patient key.";
  }
  return null;
}

function buildPendingResume(
  pUrl: string,
  intake: IntakeUser,
  facilityFromUrl: string
): { taskId: number; facilityKey: string; patientKey: string } {
  const facilityKey = (intake.facilityKey ?? facilityFromUrl).trim();
  const pTrim = pUrl.trim();
  let patientKey = "";
  if (pTrim.length === PATIENT_KEY_LEN) patientKey = pTrim;
  else {
    const ipk = (intake.patientKey ?? "").trim();
    if (ipk && !isResetPwdPatientKey(ipk)) patientKey = ipk;
  }
  const raw = intake.taskId ?? intake.taskID;
  const taskId = Number(raw);
  return {
    taskId: Number.isFinite(taskId) ? taskId : 0,
    facilityKey,
    patientKey,
  };
}

function facilityPatientForInsert(
  fUrl: string,
  pUrl: string,
  intake: IntakeUser
): { facilityKey: string; patientKey: string } {
  const fk = (intake.facilityKey ?? fUrl).trim();
  const pTrim = pUrl.trim();
  const ipk = (intake.patientKey ?? "").trim();
  const patientKey =
    ipk && !isResetPwdPatientKey(ipk)
      ? ipk
      : pTrim.length === PATIENT_KEY_LEN
        ? pTrim
        : "";
  return { facilityKey: fk, patientKey };
}

const PW_RULES = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "Contains Number", test: (v: string) => /[0-9]/.test(v) },
  { label: "Contains Uppercase", test: (v: string) => /[A-Z]/.test(v) },
  {
    label: "Contains Special Character",
    test: (v: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v),
  },
];

function PasswordRequirements({ password }: { password: string }) {
  return (
    <ul className="mt-2 space-y-1 pl-1">
      {PW_RULES.map(({ label, test }) => {
        const met = password.length > 0 && test(password);
        return (
          <li
            key={label}
            className={cn("flex items-center gap-1.5 text-xs", met ? "text-green-600" : "text-[#6F7375]")}
          >
            <span className="w-3 text-center font-bold leading-none" aria-hidden="true">
              {met ? "✓" : "•"}
            </span>
            {label}
          </li>
        );
      })}
    </ul>
  );
}

export function TaskLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, completeAuth } = useAuth();
  const setProfileAuthInfo = useProfileStore((s) => s.setProfileAuthInfo);
  const setPatientAssociated = useProfileStore((s) => s.setPatientAssociated);
  const setCurrentPatient = useProfileStore((s) => s.setCurrentPatient);
  const logoutProfile = useProfileStore((s) => s.logout);

  const key = searchParams.get("key");
  const f = searchParams.get("f");
  const p = searchParams.get("p");

  const [phase, setPhase] = useState<
    "loading" | "bad_params" | "reset" | "register" | "login_redirect"
  >("loading");
  const [intakeUser, setIntakeUser] = useState<IntakeUser | null>(null);
  const [intakeToken, setIntakeToken] = useState<string | null>(null);

  const [patientList, setPatientList] = useState<PatientOption[]>([]);
  const [showPatientSelect, setShowPatientSelect] = useState(false);
  const firstPatientButtonRef = useRef<HTMLButtonElement>(null);

  const resetForm = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: "", password: "", confirmPassword: "" },
  });
  const regForm = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [showPwReset, setShowPwReset] = useState({ pwd: false, confirm: false });
  const [showPwReg, setShowPwReg] = useState({ pwd: false, confirm: false });

  const resetPassword = useWatch({ control: resetForm.control, name: "password" });
  const regPassword = useWatch({ control: regForm.control, name: "password" });

  const continueAfterPasswordAuth = useCallback(
    async (userData: Record<string, unknown>) => {
      setProfileAuthInfo({
        firstName: (userData.firstName as string) ?? "",
        lastName: (userData.lastName as string) ?? "",
        nickName: (userData.nickName as string) ?? "",
        email: (userData.email as string) ?? "",
        ...userData,
      });
      const uid =
        (userData.id as number | undefined) ??
        (userData.userID as number | undefined);
      const patients = (
        await authRequest.request.getUserPatients(String(uid))
      ).data as PatientOption[];

      if (!patients?.length) {
        completeAuth();
        clearPendingTaskResume();
        navigate("/", { replace: true });
        return;
      }
      const pendingResume = getPendingTaskResume();
      const linkedPatient =
        patients.length > 1 && pendingResume?.patientKey?.trim()
          ? findPatientByPendingKey(patients, pendingResume)
          : undefined;

      if (patients.length === 1 || linkedPatient) {
        const chosen = (linkedPatient ?? patients[0]) as PatientOption;
        setPatientAssociated(patients as unknown as Patient[]);
        setCurrentPatient(chosen as unknown as Patient);
        completeAuth();
        await tryResumePendingTask(navigate, chosen);
        return;
      }
      setPatientList(patients);
      setShowPatientSelect(true);
    },
    [completeAuth, navigate, setCurrentPatient, setPatientAssociated, setProfileAuthInfo]
  );

  useEffect(() => {
    if (showPatientSelect) {
      firstPatientButtonRef.current?.focus();
    }
  }, [showPatientSelect]);

  useEffect(() => {
    const paramErr = validateTaskQueryParams(key, f, p);
    if (paramErr) {
      toast.error(paramErr);
      setPhase("bad_params");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await validateIntakeKey({
          intakeKey: key!.trim(),
          facilityKey: f!.trim(),
          patientKey: p!.trim(),
        });
        if (cancelled) return;
        if (!data.success) {
          toast.error(data.msg?.trim() || "This link could not be validated.");
          logoutProfile();
          navigate("/account/login", { replace: true });
          return;
        }
        if (!data.token?.trim()) {
          toast.error("This link is missing authorization. Please request a new link.");
          navigate("/account/login", { replace: true });
          return;
        }
        setIntakeUser(data);
        setIntakeToken(data.token);

        const pending = buildPendingResume(p!, data, f!);
        setPendingTaskResume(pending);

        if (data.userExists && isResetPwdPatientKey(data.patientKey)) {
          setPhase("reset");
          return;
        }
        if (data.userExists) {
          setPhase("login_redirect");
          logoutProfile();
          navigate("/account/login", { replace: true });
          return;
        }
        setPhase("register");
      } catch {
        if (!cancelled) {
          toast.error("Unable to validate this link. Please try again or log in.");
          navigate("/account/login", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, f, p, navigate, logoutProfile]);

  const onResetSubmit = async (data: ResetForm) => {
    if (!intakeToken || !key || !f) return;
    setResetSubmitting(true);
    try {
      const result = await intakeUpdateUserPwd(
        {
          email: data.email.trim(),
          pwd: data.password,
          intakeKey: key.trim(),
          facilityPatient: {
            facilityKey: f.trim(),
            patientKey: INTAKE_RESET_PATIENT_KEY,
          },
        },
        intakeToken
      );
      if (!result.success) {
        toast.error(result.reason?.trim() || "Password could not be updated.");
        clearPendingTaskResume();
        logoutProfile();
        navigate("/account/login", { replace: true });
        return;
      }
      toast.success("Password updated. Signing you in…");
      const userData = (await login(data.email.trim(), data.password)) as unknown as Record<
        string,
        unknown
      >;
      if (!userData) {
        toast.error("Sign-in failed after reset.");
        navigate("/account/login", { replace: true });
        return;
      }
      await continueAfterPasswordAuth(userData);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setResetSubmitting(false);
    }
  };

  const onRegisterSubmit = async (data: RegisterForm) => {
    if (!intakeToken || !key || !intakeUser) return;
    const { facilityKey, patientKey } = facilityPatientForInsert(f!, p!, intakeUser);
    if (!patientKey) {
      toast.error("Patient information is incomplete. Please use the link from your email.");
      return;
    }
    setRegSubmitting(true);
    try {
      const result = await intakeInsertUser(
        {
          email: data.email.trim(),
          pwd: data.password,
          intakeKey: key.trim(),
          facilityPatient: { facilityKey, patientKey },
        },
        intakeToken
      );
      if (!result.success) {
        toast.error(result.reason?.trim() || "Account could not be created.");
        clearPendingTaskResume();
        navigate("/account/login", { replace: true });
        return;
      }
      toast.success("Account created. Signing you in…");
      const userData = (await login(data.email.trim(), data.password)) as unknown as Record<
        string,
        unknown
      >;
      if (!userData) {
        toast.error("Sign-in failed.");
        navigate("/account/login", { replace: true });
        return;
      }
      await continueAfterPasswordAuth(userData);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setRegSubmitting(false);
    }
  };

  const onSelectPatient = async (patient: PatientOption) => {
    setPatientAssociated(patientList as unknown as Patient[]);
    setCurrentPatient(patient as unknown as Patient);
    setShowPatientSelect(false);
    completeAuth();
    await tryResumePendingTask(navigate, patient);
  };

  if (phase === "loading" || phase === "login_redirect") {
    return (
      <BrandedAuthShell>
        <section className="card card-featured mb-4">
          <div className="card-body text-center">
            <p className="text-sm" style={{ color: "#6F7375" }}>
              {phase === "login_redirect" ? "Redirecting to sign in…" : "Validating your link…"}
            </p>
          </div>
        </section>
      </BrandedAuthShell>
    );
  }

  if (phase === "bad_params") {
    return (
      <BrandedAuthShell>
        <section className="card card-featured mb-4">
          <div className="card-body text-center">
            <span>
              <h3 className="label">Link issue</h3>
            </span>
            <p className="text-sm" style={{ color: "#6F7375" }}>
              This link is invalid or incomplete.
            </p>
            <Link to="/account/login" className="hs-auth-link mt-3">
              Go to sign in
            </Link>
          </div>
        </section>
      </BrandedAuthShell>
    );
  }

  if (phase === "reset") {
    return (
      <BrandedAuthShell>
        <>
          <section className="card card-featured mb-4">
            <div className="card-body">
              <span>
                <h3 className="label">Reset your password</h3>
              </span>
              <form onSubmit={resetForm.handleSubmit(onResetSubmit)}>
                <div className="form-group">
                  <label htmlFor="reset-email" className="label f-weight-550">
                    Email
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="username"
                    className={cn(
                      "form-control form-control-sm",
                      resetForm.formState.errors.email && "border-red-500"
                    )}
                    {...resetForm.register("email")}
                  />
                  {resetForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-red-600">
                      {resetForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="reset-pwd" className="label f-weight-550">
                    New password
                  </label>
                  <div
                    className={cn(
                      "flex w-full min-w-0 items-stretch overflow-hidden rounded border border-[#D5DBE4] bg-white focus-within:shadow-[0_0_0_3px_rgba(42,64,100,0.25)] focus-within:border-healthsource-navy",
                      resetForm.formState.errors.password && "border-red-500 focus-within:border-red-500 focus-within:shadow-[0_0_0_3px_rgba(236,0,68,0.18)]"
                    )}
                  >
                    <input
                      id="reset-pwd"
                      type={showPwReset.pwd ? "text" : "password"}
                      autoComplete="new-password"
                      className={cn(
                        "form-control form-control-sm !rounded-none !border-0 !shadow-none flex-1 min-w-0"
                      )}
                      {...resetForm.register("password")}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwReset((s) => ({ ...s, pwd: !s.pwd }))}
                      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center px-2.5 text-[13px] font-semibold text-healthsource-gray hover:text-healthsource-dark"
                      aria-label={showPwReset.pwd ? "hide password" : "show password"}
                    >
                      {showPwReset.pwd ? "hide" : "show"}
                    </button>
                  </div>
                  <PasswordRequirements password={resetPassword ?? ""} />
                  {resetForm.formState.errors.password && (
                    <p className="mt-1 text-xs text-red-600">
                      {resetForm.formState.errors.password.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="reset-confirm" className="label f-weight-550">
                    Confirm password
                  </label>
                  <div
                    className={cn(
                      "flex w-full min-w-0 items-stretch overflow-hidden rounded border border-[#D5DBE4] bg-white focus-within:shadow-[0_0_0_3px_rgba(42,64,100,0.25)] focus-within:border-healthsource-navy",
                      resetForm.formState.errors.confirmPassword && "border-red-500 focus-within:border-red-500 focus-within:shadow-[0_0_0_3px_rgba(236,0,68,0.18)]"
                    )}
                  >
                    <input
                      id="reset-confirm"
                      type={showPwReset.confirm ? "text" : "password"}
                      autoComplete="new-password"
                      className={cn(
                        "form-control form-control-sm !rounded-none !border-0 !shadow-none flex-1 min-w-0"
                      )}
                      {...resetForm.register("confirmPassword")}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwReset((s) => ({ ...s, confirm: !s.confirm }))}
                      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center px-2.5 text-[13px] font-semibold text-healthsource-gray hover:text-healthsource-dark"
                      aria-label={showPwReset.confirm ? "hide password" : "show password"}
                    >
                      {showPwReset.confirm ? "hide" : "show"}
                    </button>
                  </div>
                  {resetForm.formState.errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {resetForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <SubmitButton
                    isSubmitting={resetSubmitting}
                    loadingText="Updating…"
                    className="btn btn-primary"
                  >
                    Update password
                  </SubmitButton>
                </div>
              </form>
            </div>
          </section>
          {showPatientSelect && (
            <PatientSelectModal
              patients={patientList}
              firstRef={firstPatientButtonRef}
              onSelect={onSelectPatient}
            />
          )}
        </>
      </BrandedAuthShell>
    );
  }

  if (phase === "register") {
    return (
      <BrandedAuthShell>
        <>
          <section className="card card-featured mb-4">
            <div className="card-body">
              <span>
                <h3 className="label">Create your portal account</h3>
              </span>
              <form onSubmit={regForm.handleSubmit(onRegisterSubmit)}>
                <div className="form-group">
                  <label htmlFor="reg-email" className="label f-weight-550">
                    Email
                  </label>
                  <input
                    id="reg-email"
                    type="email"
                    autoComplete="username"
                    className={cn(
                      "form-control form-control-sm",
                      regForm.formState.errors.email && "border-red-500"
                    )}
                    {...regForm.register("email")}
                  />
                  {regForm.formState.errors.email && (
                    <p className="mt-1 text-xs text-red-600">
                      {regForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="reg-pwd" className="label f-weight-550">
                    Password
                  </label>
                  <div
                    className={cn(
                      "flex w-full min-w-0 items-stretch overflow-hidden rounded border border-[#D5DBE4] bg-white focus-within:shadow-[0_0_0_3px_rgba(42,64,100,0.25)] focus-within:border-healthsource-navy",
                      regForm.formState.errors.password && "border-red-500 focus-within:border-red-500 focus-within:shadow-[0_0_0_3px_rgba(236,0,68,0.18)]"
                    )}
                  >
                    <input
                      id="reg-pwd"
                      type={showPwReg.pwd ? "text" : "password"}
                      autoComplete="new-password"
                      className={cn(
                        "form-control form-control-sm !rounded-none !border-0 !shadow-none flex-1 min-w-0"
                      )}
                      {...regForm.register("password")}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwReg((s) => ({ ...s, pwd: !s.pwd }))}
                      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center px-2.5 text-[13px] font-semibold text-healthsource-gray hover:text-healthsource-dark"
                      aria-label={showPwReg.pwd ? "hide password" : "show password"}
                    >
                      {showPwReg.pwd ? "hide" : "show"}
                    </button>
                  </div>
                  <PasswordRequirements password={regPassword ?? ""} />
                  {regForm.formState.errors.password && (
                    <p className="mt-1 text-xs text-red-600">
                      {regForm.formState.errors.password.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="reg-confirm" className="label f-weight-550">
                    Confirm password
                  </label>
                  <div
                    className={cn(
                      "flex w-full min-w-0 items-stretch overflow-hidden rounded border border-[#D5DBE4] bg-white focus-within:shadow-[0_0_0_3px_rgba(42,64,100,0.25)] focus-within:border-healthsource-navy",
                      regForm.formState.errors.confirmPassword && "border-red-500 focus-within:border-red-500 focus-within:shadow-[0_0_0_3px_rgba(236,0,68,0.18)]"
                    )}
                  >
                    <input
                      id="reg-confirm"
                      type={showPwReg.confirm ? "text" : "password"}
                      autoComplete="new-password"
                      className={cn(
                        "form-control form-control-sm !rounded-none !border-0 !shadow-none flex-1 min-w-0"
                      )}
                      {...regForm.register("confirmPassword")}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwReg((s) => ({ ...s, confirm: !s.confirm }))}
                      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center px-2.5 text-[13px] font-semibold text-healthsource-gray hover:text-healthsource-dark"
                      aria-label={showPwReg.confirm ? "hide password" : "show password"}
                    >
                      {showPwReg.confirm ? "hide" : "show"}
                    </button>
                  </div>
                  {regForm.formState.errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {regForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <SubmitButton
                    isSubmitting={regSubmitting}
                    loadingText="Creating account…"
                    className="btn btn-primary"
                  >
                    Create account
                  </SubmitButton>
                </div>
              </form>
            </div>
          </section>
          {showPatientSelect && (
            <PatientSelectModal
              patients={patientList}
              firstRef={firstPatientButtonRef}
              onSelect={onSelectPatient}
            />
          )}
        </>
      </BrandedAuthShell>
    );
  }

  return null;
}

function PatientSelectModal({
  patients,
  firstRef,
  onSelect,
}: {
  patients: PatientOption[];
  firstRef: RefObject<HTMLButtonElement | null>;
  onSelect: (p: PatientOption) => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E252B]/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Select Patient"
    >
      <div className="max-h-[80dvh] w-full max-w-xl overflow-auto rounded-xl border border-[#D5DBE4] bg-white p-4 shadow-[0_12px_32px_rgba(30,37,43,0.14)]">
        <PatientListPickerContent
          patients={patients}
          getKey={(p) => `${p.facilityID}-${p.patientID}`}
          onSelect={onSelect}
          firstItemRef={firstRef as Ref<HTMLButtonElement>}
        />
      </div>
    </div>
  );
}
